import React from 'react';
import { InvShell } from './InvShell';
import type { InvTabId } from './InvShell';
import type { PeriodValue } from '../../lib/period';
import { INV_LATEST, INV_LINE_QUESTIONS, INV_NUMBERS, INV_PROGRAMME, INV_PROGRAMME_STATS } from '../../invisalign/mockData';

export interface InvOverviewViewProps {
  onSelectTab?: (tab: InvTabId) => void;
  onOpenSettings?: () => void;
  onHold: boolean;
  onSetHold?: (next: boolean) => void;
  /** The report period (spec 41). */
  period?: PeriodValue;
  onPeriodChange?: (value: PeriodValue) => void;
  onOpenCalls?: () => void;
  onOpenProspects?: (stage?: string) => void;
  onEditEmails?: () => void;
  onOpenLine?: () => void;
  onOpenCommLog?: () => void;
}

const STAGE_BOXES: { key: string; label: string; sub: string; count: number; to: 'prospects' | 'calls' }[] = [
  { key: 'worth-asking', label: 'Worth asking', sub: 'Interest found · nobody has asked yet', count: INV_NUMBERS.worthAsking, to: 'prospects' },
  { key: 'learning', label: 'Learning', sub: 'Said yes · getting ELVA’s emails', count: INV_NUMBERS.learning, to: 'prospects' },
  { key: 'ready', label: 'Ready', sub: 'Did something that says they’re close', count: INV_NUMBERS.ready, to: 'calls' },
  { key: 'consult-booked', label: 'Consult booked', sub: 'ELVA has stopped · the next 3 weeks', count: INV_NUMBERS.consultBooked, to: 'prospects' },
  { key: 'asked-not-now', label: 'Asked — not now', sub: 'Asked again after 6 months', count: INV_NUMBERS.askedNotNow, to: 'prospects' },
];

export function InvOverviewView(props: InvOverviewViewProps) {
  const { onSelectTab, onOpenSettings, onHold, onSetHold, period, onPeriodChange, onOpenCalls, onOpenProspects, onEditEmails, onOpenLine, onOpenCommLog } = props;
  const n = INV_NUMBERS;
  const bars = [
    { label: 'Asked at the chair or on the phone', value: n.asked90 },
    { label: 'Said yes', value: n.saidYes },
    { label: 'Became ready', value: n.became90Ready },
    { label: 'Booked a consult', value: n.consults90 },
    { label: 'Started aligners', value: n.started90 },
  ];
  const max = n.asked90;

  return (
    <InvShell active="overview" onSelectTab={onSelectTab} onOpenSettings={onOpenSettings} onHold={onHold} onSetHold={onSetHold} period={period} onPeriodChange={onPeriodChange}>
      {/* amber strip */}
      <section className="mt-5 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--warn-amber)_35%,transparent)] bg-[var(--warn-amber-fill)] px-4 py-3">
        <span className="text-[13px] font-bold text-[var(--warn-amber)]">{n.ready} ready for a nudge</span>
        <span className="text-[12px] text-[var(--text-secondary)]">{n.readyToCall} to call · {n.readyComingIn} coming in — Orville Allison today at 5:00 pm, Sofia Delgado on 8 Oct</span>
        <button type="button" className="ml-auto text-[12.5px] font-semibold text-[var(--warn-amber)] hover:underline" onClick={onOpenCalls}>Calls →</button>
      </section>

      {/* where your prospects are now */}
      <div className="mt-5 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[14px] font-bold text-[var(--text-primary)]">Where your prospects are now</div>
            <div className="text-[11.5px] text-[var(--text-muted)]">{n.all} patients · click a stage to see who</div>
          </div>
          <button type="button" className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={() => { onOpenProspects?.(); }}>Prospects →</button>
        </div>
        <div className="mt-3 grid grid-cols-5 gap-2.5 max-[900px]:grid-cols-2">
          {STAGE_BOXES.map((b) => (
            <div key={b.key} className="rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3">
              <div className="text-[12px] font-semibold text-[var(--text-primary)]">{b.label}</div>
              <div className="mt-1 font-[var(--font-mono)] text-[22px] font-bold text-[var(--text-primary)]">{b.count}</div>
              <div className="mt-0.5 text-[10.5px] leading-[1.4] text-[var(--text-muted)]">{b.sub}</div>
              <button type="button" onClick={() => { if (b.to === 'calls') onOpenCalls?.(); else onOpenProspects?.(b.key); }} className="mt-1.5 text-[11px] font-semibold text-[var(--brand-indigo)] hover:underline">
                {b.to === 'calls' ? 'Calls →' : 'See who →'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* the headline cases number — the period now follows the selector (spec 41) */}
      <div className="mt-4 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">
        <div className="pc-eyebrow text-[var(--text-muted)]">Consults booked</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-[var(--font-display)] text-[28px] font-semibold text-[var(--text-primary)]">{n.consults90}</span>
          <span className="text-[13px] text-[var(--text-secondary)]">consults booked · {n.started90} started aligners · {n.casesValue} in cases</span>
        </div>
        <div className="mt-3 flex flex-col gap-1.5">
          {bars.map((b) => (
            <div key={b.label} className="flex items-center gap-2">
              <span className="w-[220px] shrink-0 text-[11.5px] text-[var(--text-secondary)]">{b.label}</span>
              <span className="h-3 rounded-full bg-[var(--brand-indigo)]" style={{ width: `${String((b.value / max) * 260)}px`, minWidth: 8 }} />
              <span className="font-[var(--font-mono)] text-[12px] font-bold text-[var(--text-primary)]">{b.value}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 border-t border-[var(--border-default)] pt-2 text-[11.5px] text-[var(--text-muted)]">
          Consults booked after: a call from your team {n.bookedAfterCall} · the booking link in an email {n.bookedAfterLink} · the Invis-Line {n.bookedAfterLine}
        </div>
      </div>

      {/* 01 the programme */}
      <Card className="mt-4">
        <div className="flex items-start justify-between">
          <SectionHeading n="01" title="The programme" sub={`10 emails and 3 texts · ${String(INV_PROGRAMME_STATS.saidYes)} of the ${String(INV_PROGRAMME_STATS.total)} in the programme said yes to texts`} />
          <button type="button" className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={onEditEmails}>Edit in Settings →</button>
        </div>
        <div className="grid grid-cols-[52px_1fr_64px_92px] gap-x-3 border-b border-[var(--border-default)] pb-1.5 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">
          <span>Day</span><span>Message</span><span className="text-right">Sent</span><span className="text-right">Opened / Replied</span>
        </div>
        {INV_PROGRAMME.map((m) => (
          <div key={m.id} className="grid grid-cols-[52px_1fr_64px_92px] items-center gap-x-3 border-t border-[var(--border-default)] py-2 text-[12.5px] first:border-0">
            <span className="font-[var(--font-mono)] text-[var(--text-muted)]">{m.day}</span>
            <span className="flex items-center gap-2 text-[var(--text-secondary)]">
              <span aria-hidden className={m.kind === 'text' ? 'text-[var(--brand-indigo)]' : 'text-[var(--text-muted)]'}>{m.kind === 'text' ? '💬' : '✉️'}</span>
              <span>{m.title}</span>
            </span>
            <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{m.sent ?? '—'}</span>
            <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">
              {m.kind === 'text' ? `${String(m.replied ?? 0)} replied` : (m.opened ?? '—')}
            </span>
          </div>
        ))}
        <div className="mt-2 text-[11.5px] text-[var(--text-muted)]">When your team logs a concern, the email that answers it goes next. Texts go only to patients who said yes.</div>
      </Card>

      {/* 02 Invis-Line + 03 latest */}
      <div className="mt-4 grid grid-cols-2 gap-4 max-[900px]:grid-cols-1">
        <Card>
          <div className="flex items-start justify-between">
            <SectionHeading n="02" title="Invis-Line" sub="Last 30 days" />
            <button type="button" className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={onOpenLine}>Open →</button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Pill v={n.lineConversations} l="conversations" />
            <Pill v={n.lineBecameReady} l="became ready" />
            <Pill v={n.lineBooked} l="booked" />
          </div>
          <div className="mt-3 flex flex-col gap-1">
            {INV_LINE_QUESTIONS.slice(0, 5).map((q) => (
              <div key={q.label} className="flex items-baseline justify-between text-[12px]">
                <span className="text-[var(--text-secondary)]">{q.label}</span>
                <span className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">{q.count}</span>
              </div>
            ))}
          </div>
          <div className="mt-2.5 rounded-[8px] bg-[var(--warn-amber-fill)] px-3 py-1.5 text-[11.5px] font-semibold text-[var(--warn-amber)]">{n.lineUnanswered} questions it couldn’t answer</div>
        </Card>
        <Card>
          <div className="flex items-start justify-between">
            <SectionHeading n="03" title="Latest" sub="" />
            <button type="button" className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={onOpenCommLog}>Communication log →</button>
          </div>
          <div className="flex flex-col">
            {INV_LATEST.map((it, i) => (
              <div key={`${it.at}-${String(i)}`} className="flex items-baseline justify-between gap-3 border-t border-[var(--border-default)] py-2 first:border-0 text-[12px]">
                <span className="text-[var(--text-secondary)]">{it.body}</span>
                <span className="shrink-0 font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">{it.at}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </InvShell>
  );
}

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)] ${className ?? ''}`}>{children}</div>;
}
function SectionHeading({ n, title, sub }: { n: string; title: string; sub: string }) {
  return (
    <div className="mb-2.5 flex flex-wrap items-baseline gap-2">
      <span className="font-[var(--font-mono)] text-[11px] font-bold text-[var(--brand-indigo)]">{n}</span>
      <h2 className="m-0 text-[14px] font-bold text-[var(--text-primary)]">{title}</h2>
      {sub !== '' ? <span className="text-[11.5px] text-[var(--text-muted)]">{sub}</span> : null}
    </div>
  );
}
function Pill({ v, l }: { v: number; l: string }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-full border border-[var(--border-default)] bg-[var(--surface-sunken)] px-3 py-1 text-[12px]">
      <span className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">{v}</span>
      <span className="text-[var(--text-secondary)]">{l}</span>
    </span>
  );
}
